/**
 * The visitor's cookie choices. Strictly necessary storage needs no consent;
 * every optional category below is written only after the visitor opts in,
 * and wiped when they opt out. The Cookie Policy lists the same keys — keep
 * the two in step.
 */

export type OptionalCategory = "preferences" | "functional";

export interface Consent {
  version: number;
  preferences: boolean;
  functional: boolean;
  /** ISO timestamp of the choice; it lapses after CONSENT_MAX_AGE_DAYS. */
  decidedAt: string;
}

const KEY = "aistyle:cookie-consent";
/** Bump when a new optional cookie is added, so everyone is asked again. */
const VERSION = 1;
export const CONSENT_MAX_AGE_DAYS = 365;

/** Browser storage each optional category writes. */
const STORAGE_BY_CATEGORY: Record<OptionalCategory, string[]> = {
  preferences: ["aistyle:lang"],
  functional: ["ms-gallery", "ms-video-gallery"],
};

const CHANGE_EVENT = "aistyle:consent-change";
const OPEN_SETTINGS_EVENT = "aistyle:open-cookie-settings";

/** The stored choice, or null when the visitor has not chosen (or it lapsed). */
export function readConsent(): Consent | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const consent = JSON.parse(raw) as Consent;
    const ageMs = Date.now() - new Date(consent.decidedAt).getTime();
    if (consent.version !== VERSION || !(ageMs < CONSENT_MAX_AGE_DAYS * 86_400_000)) return null;
    return consent;
  } catch {
    return null;
  }
}

export function hasConsent(category: OptionalCategory): boolean {
  return readConsent()?.[category] === true;
}

/** Records the choice and deletes whatever a declined category had stored. */
export function saveConsent(choice: Pick<Consent, OptionalCategory>): void {
  const consent: Consent = { version: VERSION, ...choice, decidedAt: new Date().toISOString() };
  try {
    localStorage.setItem(KEY, JSON.stringify(consent));
    for (const category of Object.keys(STORAGE_BY_CATEGORY) as OptionalCategory[]) {
      if (!consent[category]) STORAGE_BY_CATEGORY[category].forEach((k) => localStorage.removeItem(k));
    }
  } catch {
    /* storage blocked — nothing optional can be stored anyway */
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function onConsentChange(listener: () => void): () => void {
  window.addEventListener(CHANGE_EVENT, listener);
  return () => window.removeEventListener(CHANGE_EVENT, listener);
}

/** Reopens the settings dialog — from the footer or the Cookie Policy. */
export function openCookieSettings(): void {
  window.dispatchEvent(new Event(OPEN_SETTINGS_EVENT));
}

export function onOpenCookieSettings(listener: () => void): () => void {
  window.addEventListener(OPEN_SETTINGS_EVENT, listener);
  return () => window.removeEventListener(OPEN_SETTINGS_EVENT, listener);
}
